import { datadogLogs } from '@datadog/browser-logs';
import { FC, MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { IListCheckEntry } from 'src/ts/interfaces/listCheck';
import { ITextBoxesDisplay, ITextBoxesEvidence } from 'src/ts/interfaces/textBoxes';
import { ListCheckTabButton } from '../../../styles';
import { FraudTrendEvidenceResult } from '../../../hooks/useFraudTrendEvidence';
import { useViewerModeTime } from '../../../hooks/useViewerModeTime';
import { closePdf } from './pdfDocument';
import { useFreshPdfUrl } from './useFreshPdfUrl';
import PdfPageImage, { PageImageProblem } from './PdfPageImage';
import {
  AlsoFound,
  Box,
  Chip,
  ImageSwitch,
  Legend,
  LegendItem,
  OrphanChip,
  PageFrame,
  PageLabel,
  PaneNotice,
  Pager,
  Pages,
  RowCard,
  RowNote,
  Rows,
  TextBoxesControls,
  TextBoxesHead,
  TextBoxesSpacer,
  TextBoxesTabs,
  Tooltip
} from './styles';
import { textBoxesCopy } from './textBoxesCopy';
import {
  blankText,
  BoxMark,
  buildModel,
  flaggedOn,
  noteFor,
  Chip as ChipModel,
  KindStyle,
  PageModel,
  primaryMark,
  rowsOf,
  TextBoxesModel
} from './textBoxesModel';

type View = 'page' | 'list';

const SCROLLER = '[data-scroll-root]';
const JUMP_PAUSE_MS = 700;
const TIP_ROOM = 300;

const flip = (past: boolean) => (past ? 'calc(-100% - 28px)' : '0');

const TAB_ID: Record<View, string> = { page: 'text-boxes-tab-page', list: 'text-boxes-tab-lines' };

const NO_DISPLAY: ITextBoxesDisplay = { provider: null, kinds: {}, also_found: {} };

interface TextBoxesPaneProps {
  entry: IListCheckEntry;
  evidence: FraudTrendEvidenceResult;
  proofId: string | undefined;
  pdfUrl: string | null | undefined;
}

interface TipItem {
  style: KindStyle | null;
  text: string;
}

interface TipState {
  x: number;
  y: number;
  items: TipItem[];
}

const pct = (value: number, of: number) => `${((value / of) * 100).toFixed(3)}%`;

const tipFor = (model: TextBoxesModel, marks: BoxMark[], text: string): TipItem[] => {
  const shownText = text.trim() ? text.trim() : text ? textBoxesCopy.blankSpaces : '';
  if (marks.length === 0) return [{ style: null, text: shownText }];
  const ordered = [...marks].sort((a, b) => Number(b.rule) - Number(a.rule));
  return ordered.map((mark) => ({ style: model.kindStyle(mark.kind), text: shownText }));
};

const PageBoxes: FC<{
  model: TextBoxesModel;
  page: PageModel;
  showText: boolean;
  image: ReactNode;
}> = ({ model, page, showText, image }) => (
  <PageFrame $ratio={page.width / page.height} $showText={showText} data-page={page.page}>
    {image}
    {page.pieces.map((piece, index) => {
      const [x0, y0, x1, y1] = piece.bbox;
      const mark = primaryMark(piece.marks);
      const style = mark ? model.kindStyle(mark.kind) : null;
      return (
        <Box
          key={`p${index}`}
          data-tb={`p:${page.page}:${index}`}
          $color={style?.color ?? null}
          $font={model.font}
          $showText={showText}
          style={{
            left: pct(x0, page.width),
            top: pct(y0, page.height),
            width: pct(x1 - x0, page.width),
            height: pct(y1 - y0, page.height),
            fontSize: `calc(${((y1 - y0) / page.width).toFixed(5)} * 100cqw)`
          }}
        >
          {piece.text}
        </Box>
      );
    })}
    {page.orphans.map((orphan, index) => {
      const [x0, y0, x1, y1] = orphan.bbox;
      return (
        <Box
          key={`o${index}`}
          data-tb={`o:${page.page}:${index}`}
          $color={model.kindStyle(orphan.mark.kind).color}
          $font={model.font}
          $showText={false}
          $dashed
          style={{
            left: pct(x0, page.width),
            top: pct(y0, page.height),
            width: pct(x1 - x0, page.width),
            height: pct(y1 - y0, page.height),
            zIndex: 2
          }}
        />
      );
    })}
  </PageFrame>
);

const RowChip: FC<{ model: TextBoxesModel; chip: ChipModel }> = ({ model, chip }) => {
  const style = chip.mark ? model.kindStyle(chip.mark.kind) : null;
  const note = style && chip.mark ? noteFor(style, chip.mark.signal) : null;
  if (chip.orphan) {
    return (
      <OrphanChip $color={style?.color ?? null} $font={model.font} $dashed>
        {textBoxesCopy.empty}
      </OrphanChip>
    );
  }
  const blank = !chip.text.trim();
  return (
    <>
      {style && note && style.note_at === 'before' && (
        <RowNote $color={style.color} $kind="gap">
          {note}
        </RowNote>
      )}
      <Chip $color={style?.color ?? null} $font={model.font} $blank={blank}>
        {blank ? blankText(chip.text) : chip.text}
      </Chip>
      {style && note && style.note_at === 'after' && (
        <RowNote $color={style.color} $kind="step">
          {note}
        </RowNote>
      )}
    </>
  );
};

const LineByLine: FC<{ model: TextBoxesModel }> = ({ model }) => (
  <Rows>
    {model.pages.map((page) => (
      <div key={page.page} data-page={page.page}>
        {model.pages.length > 1 && (
          <PageLabel>{textBoxesCopy.pageOf(page.page, model.pages.length)}</PageLabel>
        )}
        <Rows>
          {rowsOf(page).map((row, index) => {
            const style = row.mark ? model.kindStyle(row.mark.kind) : null;
            return (
              <RowCard key={index} $color={style?.color ?? null} data-testid="text-boxes-row">
                {row.chips.map((chip, chipIndex) => (
                  <RowChip key={chipIndex} model={model} chip={chip} />
                ))}
              </RowCard>
            );
          })}
        </Rows>
      </div>
    ))}
  </Rows>
);

export const TextBoxesView: FC<{
  model: TextBoxesModel;
  proofId: string | undefined;
  pdfUrl: string | null | undefined;
}> = ({ model, proofId, pdfUrl: storedUrl }) => {
  const { url: pdfUrl, retry } = useFreshPdfUrl(proofId, storedUrl);
  const refreshing = useRef(false);
  const [view, setView] = useState<View>('page');
  useViewerModeTime(view === 'page' ? 'text_boxes_page' : 'text_boxes_lines', true, 'inspector');
  const [showImage, setShowImage] = useState(true);
  const [badPages, setBadPages] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(model.pages[0]?.page ?? 1);
  const [tip, setTip] = useState<TipState | null>(null);
  const pagesRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const jumpUntil = useRef(0);
  const multiPage = model.pages.length > 1;
  const switchEnabled = Boolean(pdfUrl) && badPages.size === 0;
  const anyImage = Boolean(pdfUrl) && showImage && badPages.size < model.pages.length;

  const onProblem =
    (page: number) => (problem: PageImageProblem, detail: Record<string, number | string>) => {
      if (refreshing.current) return;
      if (problem === 'render_failed' && detail.status === 403 && retry()) {
        refreshing.current = true;
        return;
      }
      datadogLogs.logger.warn('text_boxes_page_image_hidden', {
        proof_id: proofId,
        problem,
        ...detail
      });
      setBadPages((pages) => new Set(pages).add(page));
    };

  useEffect(() => {
    refreshing.current = false;
    setBadPages(new Set());
    return () => (pdfUrl ? closePdf(pdfUrl) : undefined);
  }, [pdfUrl]);

  useEffect(() => {
    const scroller = pagesRef.current?.closest<HTMLElement>(SCROLLER);
    if (!scroller || !multiPage) return undefined;
    const onScroll = () => {
      if (Date.now() < jumpUntil.current) return;
      const headerBottom = headRef.current?.getBoundingClientRect().bottom ?? 0;
      let page = model.pages[0].page;
      pagesRef.current?.querySelectorAll<HTMLElement>('[data-page]').forEach((el) => {
        if (el.getBoundingClientRect().top <= headerBottom + 1) page = Number(el.dataset.page);
      });
      setCurrentPage(page);
    };
    onScroll();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [view, multiPage, model]);

  const goToPage = (page: number) => {
    jumpUntil.current = Date.now() + JUMP_PAUSE_MS;
    setCurrentPage(page);
    const scroller = pagesRef.current?.closest<HTMLElement>(SCROLLER);
    const target = pagesRef.current?.querySelector<HTMLElement>(`[data-page="${page}"]`);
    const header = headRef.current?.getBoundingClientRect().bottom;
    if (!scroller || !target || header === undefined) return;
    scroller.scrollTo({
      top: scroller.scrollTop + target.getBoundingClientRect().top - header,
      behavior: 'smooth'
    });
  };

  const onMouseMove = (event: MouseEvent) => {
    if (view !== 'page' || typeof document.elementsFromPoint !== 'function') return;
    const items = document
      .elementsFromPoint(event.clientX, event.clientY)
      .map((el) => (el as HTMLElement).dataset?.tb)
      .filter((id): id is string => Boolean(id))
      .flatMap((id) => {
        const [type, pageNo, index] = id.split(':');
        const page = model.pages.find((p) => p.page === Number(pageNo));
        if (!page) return [];
        if (type === 'o') {
          return tipFor(model, [page.orphans[Number(index)].mark], '');
        }
        const piece = page.pieces[Number(index)];
        return tipFor(model, piece.marks, piece.text);
      })
      .sort((a, b) => Number(Boolean(b.style)) - Number(Boolean(a.style)));
    setTip(items.length ? { x: event.clientX, y: event.clientY, items } : null);
  };

  const pageIndex = model.pages.findIndex((p) => p.page === currentPage);

  return (
    <>
      <TextBoxesHead ref={headRef}>
        <PaneNotice>{textBoxesCopy.lede(model.providerName, view === 'list')}</PaneNotice>
        <TextBoxesControls>
          <TextBoxesTabs role="tablist">
            {(['page', 'list'] as View[]).map((option) => (
              <ListCheckTabButton
                key={option}
                type="button"
                role="tab"
                id={TAB_ID[option]}
                data-testid={TAB_ID[option]}
                aria-selected={view === option}
                $active={view === option}
                onClick={() => setView(option)}
              >
                {option === 'page' ? textBoxesCopy.onThePage : textBoxesCopy.lineByLine}
              </ListCheckTabButton>
            ))}
          </TextBoxesTabs>
          {view === 'page' && (
            <ImageSwitch>
              <input
                type="checkbox"
                id="text-boxes-page-image"
                data-testid="text-boxes-page-image"
                checked={anyImage}
                disabled={!switchEnabled}
                onChange={(event) => setShowImage(event.target.checked)}
              />
              <i />
              {textBoxesCopy.pageImage}
            </ImageSwitch>
          )}
          <TextBoxesSpacer />
          {multiPage && (
            <Pager data-testid="text-boxes-pager">
              <button
                type="button"
                id="text-boxes-page-prev"
                data-testid="text-boxes-page-prev"
                aria-label={textBoxesCopy.previousPage}
                disabled={pageIndex <= 0}
                onClick={() => goToPage(model.pages[pageIndex - 1].page)}
              >
                ‹
              </button>
              <span>{textBoxesCopy.pageOf(currentPage, model.pages.length)}</span>
              <em data-testid="text-boxes-page-flagged">
                {textBoxesCopy.flagged(flaggedOn(model.pages[Math.max(pageIndex, 0)]))}
              </em>
              <button
                type="button"
                id="text-boxes-page-next"
                data-testid="text-boxes-page-next"
                aria-label={textBoxesCopy.nextPage}
                disabled={pageIndex >= model.pages.length - 1}
                onClick={() => goToPage(model.pages[pageIndex + 1].page)}
              >
                ›
              </button>
            </Pager>
          )}
        </TextBoxesControls>
        <Legend data-testid="text-boxes-legend">
          <LegendItem $color={null}>
            <Chip $color={null} $font={model.font}>
              {model.normalSample || ' '}
            </Chip>
            <span>
              <b>{textBoxesCopy.normal}</b>
              <small>{textBoxesCopy.asWritten(model.providerName)}</small>
            </span>
          </LegendItem>
          {model.legend.map((entry) => (
            <LegendItem key={entry.kind} $color={entry.color} data-testid={`legend-${entry.kind}`}>
              <Chip $color={entry.color} $font={model.font} $dashed={entry.removed}>
                {entry.sample || ' '}
              </Chip>
              <span>
                <span>
                  <b>{entry.headline}</b>
                  <em>{entry.count.toLocaleString('en-US')}</em>
                </span>
                {entry.description && <small>{entry.description}</small>}
              </span>
            </LegendItem>
          ))}
        </Legend>
      </TextBoxesHead>
      <div ref={pagesRef} onMouseMove={onMouseMove} onMouseLeave={() => setTip(null)}>
        {view === 'page' ? (
          <Pages data-testid="text-boxes-pages">
            {model.pages.map((page) => {
              const rendered = Boolean(pdfUrl) && !badPages.has(page.page);
              return (
                <div key={page.page}>
                  {multiPage && (
                    <PageLabel>{textBoxesCopy.pageOf(page.page, model.pages.length)}</PageLabel>
                  )}
                  <PageBoxes
                    model={model}
                    page={page}
                    showText={!(rendered && showImage)}
                    image={
                      rendered && pdfUrl ? (
                        <PdfPageImage
                          url={pdfUrl}
                          page={page.page}
                          width={page.width}
                          height={page.height}
                          onProblem={onProblem(page.page)}
                        />
                      ) : null
                    }
                  />
                </div>
              );
            })}
          </Pages>
        ) : (
          <LineByLine model={model} />
        )}
      </div>
      {model.alsoFound.length > 0 && (
        <AlsoFound data-testid="text-boxes-also-found">
          <b>{textBoxesCopy.alsoFound}</b>
          {model.alsoFound.map((label) => (
            <span key={label}>{label}</span>
          ))}
        </AlsoFound>
      )}
      {tip && (
        <Tooltip
          role="tooltip"
          style={{
            left: tip.x + 14,
            top: tip.y + 14,
            transform: `translate(${flip(tip.x > window.innerWidth - TIP_ROOM)}, ${flip(
              tip.y > window.innerHeight - TIP_ROOM
            )})`
          }}
        >
          {tip.items.map((item, index) => (
            <div key={index}>
              <b>{item.style ? item.style.headline : textBoxesCopy.normal}</b>
              {item.style?.description && <span>{item.style.description}</span>}
              {item.text && <code>{item.text}</code>}
            </div>
          ))}
        </Tooltip>
      )}
    </>
  );
};

const TextBoxesPane: FC<TextBoxesPaneProps> = ({ entry, evidence, proofId, pdfUrl }) => {
  const status = entry.has_evidence ? evidence.status : 'missing';
  const display = entry.display ?? NO_DISPLAY;
  const model = useMemo(
    () =>
      status === 'success' && evidence.data
        ? buildModel(evidence.data as unknown as ITextBoxesEvidence, display)
        : null,
    [status, evidence, display]
  );

  if (status === 'idle' || status === 'loading') {
    return <PaneNotice>{textBoxesCopy.loading}</PaneNotice>;
  }
  if (status === 'missing') return <PaneNotice>{textBoxesCopy.missing}</PaneNotice>;
  if (!model) return <PaneNotice>{textBoxesCopy.failed}</PaneNotice>;
  if (model.pages.length === 0) return <PaneNotice>{textBoxesCopy.missing}</PaneNotice>;

  return (
    <div data-testid="text-boxes-pane">
      <TextBoxesView model={model} proofId={proofId} pdfUrl={pdfUrl} />
    </div>
  );
};

export default TextBoxesPane;
